import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { DynamicFlowService } from 'src/app/services/dynamic-flow.service';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AccountService } from 'src/app/services/account.service';
import { LoginService } from 'src/app/services/login.service';

@Injectable()
export class Auth0TokenInterceptor implements HttpInterceptor {

  auth0locale: string = 'en';  //Default to English
  locale: string = 'en';  //Default to English

  constructor(
    private auth: AuthService,
    private userService: UserService,
    private dynamicFlowService: DynamicFlowService,
    private router: Router,
    private accountService: AccountService,
    private loginService: LoginService
  ) { }

  private logInternalResult(req: HttpRequest<any>, response: string): void {
    try {
      if (this.userService.isInternalUser()) {
        this.logInternal(req, `${req.method} ${req.urlWithParams}`, response);
      }
    } catch {
      console.log('Error Logging Internal User');
    }
  }

  uilogInternalUser(request: string, response: string): void {
    try {
      const userDetails = this.userService.getUserInfo();
      const policyDetails = this.loginService.getPolicyDetails();
      this.accountService.uiLogInternalUser({
        url: this.router.url,
        payload: policyDetails?.policyNumber
          ? `PolicyNumber: ${policyDetails.policyNumber}` : '',
        message: `Request: ${request}, Response: ${response}`,
        email: userDetails?.emailAddress || ''
      }).subscribe({
        error: error => console.log(error)
      });
    } catch {
      console.log('Error Logging Internal User');
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip token check for Auth0 domain requests and public assets
    if (this.shouldSkip(req.url)) {
      return next.handle(req);
    }

    return this.auth.getAccessTokenSilently().pipe(
      switchMap(() => {
        // Token is valid (or was refreshed), proceed with the request
        //return next.handle(req);
        const startTime = Date.now();
        return next.handle(req).pipe(
          tap({
            next: event => {
              if (event instanceof HttpResponse) {
                this.logInternalResult(req,
                  `${event.status} - ${event.statusText} in ${Date.now() - startTime}ms`);
              }
            },
            error: error => {
              this.logInternalResult(req,
                error instanceof HttpErrorResponse ? error.message : '');
            }
          })
        );
      }),
      catchError((err) => {
        if ((err && err.status && err.status !== 401) || (err && err.status == 0)) {
          // Non-auth failures from the API should be propagated, not replayed.
          return throwError(() => err);
        }
        this.determineLocale();
        // Session expired and couldn't be refreshed — redirect to login
        let userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
        if (userDetails && userDetails.objectUID && userDetails.objectUID.startsWith('auth0|')) {
          //This is a LOGGED-IN user (Normal ONL Login, not passwordless) So, have them login again to get a new user session.
          if (this.auth0locale && this.auth0locale == 'en-CA') {
            this.auth.loginWithRedirect({
              authorizationParams: {
                ui_locales: this.auth0locale,  //Set the locale here as needed
                redirect_uri: environment.auth0.authorizationParams.redirect_uri,
                'ext-style': 'canada'
              }
            });
          } else {
            this.auth.loginWithRedirect({
              authorizationParams: {
                ui_locales: this.auth0locale,  //Set the locale here as needed
                redirect_uri: environment.auth0.authorizationParams.redirect_uri,
                'ext-lang': this.locale
              }
            });
          }
        } else {
          //This is a passwordless user, first check they have an auth0 token that starts with email|
          let sspDetails = sessionStorage.getItem('partnerDetails');
          let parsedSSPDetails = sspDetails ? JSON.parse(sspDetails) : null;  //This will be there for users coming from landing page
          let channelPartner = this.dynamicFlowService.setChannelPartner();   //This will be there for users coming from landing page
          let guestDetails = sessionStorage.getItem('partnerName');           //This will be there for users coming from landing page
          let guestDetailsParsed = guestDetails ? JSON.parse(guestDetails) : null;

          //For SSP Users and Guest Users
          if (parsedSSPDetails && parsedSSPDetails.partnerName) {
            let partner = parsedSSPDetails.partnerName;
            sessionStorage.setItem('ssp-landing-page', `/${partner}`);
            this.auth.loginWithRedirect({
              authorizationParams: {
                connection: 'email',
                redirect_uri: `${environment.baseUrl.web}callback`,
                'ext-connect': 'email' //additional param to indicate template to hide "sign up" and "login" text
                // redirect_uri: 'http://localhost:4200/callback'
              },
              appState: { target: `/${partner}` }
            });
          } else if (channelPartner && channelPartner != ' ') { //For CCP Users
            this.auth.loginWithRedirect({
              authorizationParams: {
                connection: 'email',
                redirect_uri: `${environment.baseUrl.web}callback`,
                'ext-connect': 'email'  //additional param to indicate template to hide "sign up" and "login" text
                //redirect_uri: `${'http://localhost:4200/'}callback`
              },
              appState: { target: `/${channelPartner}` }
            });
          } else if (guestDetailsParsed) {
            let guestPath = guestDetailsParsed; //Not really needed because it is already stored in partner-name session variable. But keeping here just in case.
            this.auth.loginWithRedirect({
              authorizationParams: {
                connection: 'email',
                // redirect_uri: 'http://localhost:4200/callback',
                redirect_uri: `${environment.baseUrl.web}callback`,
                'ext-connect': 'email' //additional param to indicate template to hide "sign up" and "login" text
              },
              appState: { target: '/guestVerify' }
            });
          }


          //If they get down here, it could possibly be this type of user: TFCP / Internal / TCCP
        }
        return throwError(() => err);
      })
    );
  }

  private shouldSkip(url: string): boolean {
    url = url.toLowerCase();
    // Skip for Auth0 endpoints, local assets, and recaptcha
    return url.includes('oauth/token')
      || url.includes('assets/')
      || url.includes('recaptcha')
      || url.includes('mychoice/decryptuserdetails')
      || url.includes('policy/partner-customization')
      || url.includes('save-customer-profile')
      || url.includes('backchannel-logout');
  }

  private determineLocale() {
    this.userService.getLocale().subscribe(
      locale => {
        if (locale && locale.toLowerCase() != 'en-ca') {
          this.auth0locale = locale.split('-')[0]; //Get only the language part
          this.locale = locale;
        } else if (locale && locale.toLowerCase() == 'en-ca') {
          this.auth0locale = 'en-CA';  //They do want hyphen part for en-CA. You can find exactly what they are looking for each language when going to Autho0 homepage, and the Settings in the bottom left corner. Tenant Settings, and then at the very bottom of those are the language options.
          this.locale = locale;
        } else {
          this.auth0locale = 'en'; //Default to English
          this.locale = 'en';
        }
        sessionStorage.setItem('locale', this.locale);  //Unsure if this is actually needed, but it seemed to help, and seemed to not work with this not here...
      }, error => {
        this.auth0locale = 'en'; //Default to English
        this.locale = 'en';
        sessionStorage.setItem('locale', this.locale);  //Unsure if this is actually needed, but it seemed to help, and seemed to not work with this not here...
      }
    );
  }

  logInternal(req: HttpRequest<any>, request: any, response: any) {
    if (!req.urlWithParams.includes('/uilog/internal') && !req.urlWithParams.includes('claim/claimids') && !req.urlWithParams.includes('claim/GetStats')
      && !req.urlWithParams.includes('claim/summary') && !req.urlWithParams.includes('claim/details') && !req.urlWithParams.includes('claim/MessageID') &&
      (req.urlWithParams.includes('claim') || req.urlWithParams.includes('claim/save') || req.urlWithParams.includes('claim/submit') ||
        req.urlWithParams.includes('users/invite') || req.urlWithParams.includes('users/updateUser') ||
        req.urlWithParams.includes('admin/updateUser') || req.urlWithParams.includes('admin/deleteUser') ||
        req.urlWithParams.includes('admin/deleteTempUser') || req.urlWithParams.includes('claim/postCustomerMessage') ||
        req.urlWithParams.includes('digitalWallet/getSummary') || req.urlWithParams.includes('DPAT/getRedirect') ||
        req.urlWithParams.includes('digitalWallet/startSession') || req.urlWithParams.includes('policy/renewal-questionnaire') ||
        req.urlWithParams.includes('policy/UpdateContact') || req.urlWithParams.includes('billing/TokenProcessing') ||
        req.urlWithParams.includes('documents/downloadDocument') || req.urlWithParams.includes('HVCMSession') ||
        req.urlWithParams.includes('documents/DownloadExcelInvoice') || req.urlWithParams.includes('documents/DownloadInvoice') ||
        req.urlWithParams.includes('billing/PayorInvoice') || req.urlWithParams.includes('DPAT/retrievePaymentMethod') ||
        req.urlWithParams.includes('claim/withdraw') || req.urlWithParams.includes('claim/reopen'))) {
      this.uilogInternalUser(request, response);
    }
  }
}
